import React, { useState, useEffect } from 'react';
import { useMenuIcon } from '@/hooks/useMenuIcon';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Plus,
  Trash2,
  Save,
  X,
  ChevronRight,
  ChevronDown,
  Menu as MenuIcon,
  AlertCircle,
  CheckCircle2,
  Power,
  PowerOff,
} from 'lucide-react';
import { API_BASE_URL as BASE } from '@/api/apiConfig';

const API_BASE_URL = `${BASE}/api`;

interface MenuItem {
  id: number;
  menuCode: string;
  menuName: string;
  menuPath: string | null;
  menuIcon: string | null;
  parentId: number | null;
  displayOrder: number;
  menuLevel: number;
  description: string | null;
  requiredRole: 'SUPER_ADMIN' | 'CLIENT';
  isActive: boolean;
  isExternal: boolean;
  createdAt: string;
  updatedAt: string | null;
  children: MenuItem[];
}

interface MenuFormData {
  menuCode: string;
  menuName: string;
  menuPath: string;
  menuIcon: string;
  parentId: number | null;
  displayOrder: number;
  menuLevel: number;
  description: string;
  requiredRole: 'SUPER_ADMIN' | 'CLIENT';
  isActive: boolean;
  isExternal: boolean;
}

export const MenuManagement: React.FC = () => {
  const menuIcon = useMenuIcon();
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedMenu, setSelectedMenu] = useState<MenuItem | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [savingField, setSavingField] = useState<string | null>(null);
  const [savedField, setSavedField] = useState<string | null>(null);
  const [expandedMenus, setExpandedMenus] = useState<Set<number>>(new Set());
  const [formData, setFormData] = useState<MenuFormData>({
    menuCode: '',
    menuName: '',
    menuPath: '',
    menuIcon: '',
    parentId: null,
    displayOrder: 1,
    menuLevel: 0,
    description: '',
    requiredRole: 'CLIENT',
    isActive: true,
    isExternal: false,
  });

  useEffect(() => {
    loadMenuItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const getToken = () => {
    return localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  };

  const findMenuById = (items: MenuItem[], id: number): MenuItem | null => {
    for (const item of items) {
      if (item.id === id) return item;
      if (item.children && item.children.length > 0) {
        const found = findMenuById(item.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  // 평면 리스트를 트리 구조로 변환
  const buildMenuTree = (flatList: MenuItem[]): MenuItem[] => {
    console.log('🔧 Building tree from flat list:', flatList.length, 'items');
    
    // 1. 모든 메뉴를 ID로 매핑
    const menuMap = new Map<number, MenuItem>();
    flatList.forEach((menu) => {
      // children 초기화
      menuMap.set(menu.id, { ...menu, children: [] });
    });
    
    // 2. 부모-자식 관계 설정
    const rootMenus: MenuItem[] = [];
    menuMap.forEach((menu) => {
      if (menu.parentId === null || menu.parentId === undefined) {
        // 최상위 메뉴
        rootMenus.push(menu);
        console.log('  📌 Root menu:', menu.menuName, '(id:', menu.id, 'displayOrder:', menu.displayOrder, ')');
      } else {
        // 자식 메뉴
        const parent = menuMap.get(menu.parentId);
        if (parent) {
          if (!parent.children) parent.children = [];
          parent.children.push(menu);
          console.log('  👶 Child menu:', menu.menuName, '→ parent:', parent.menuName);
        } else {
          console.warn('  ⚠️ Parent not found for:', menu.menuName, '(parentId:', menu.parentId, ')');
          // 부모를 찾을 수 없으면 루트로 처리
          rootMenus.push(menu);
        }
      }
    });
    
    // 3. 각 레벨에서 displayOrder로 정렬
    const sortByDisplayOrder = (items: MenuItem[]) => {
      items.sort((a, b) => a.displayOrder - b.displayOrder);
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          sortByDisplayOrder(item.children);
        }
      });
    };
    sortByDisplayOrder(rootMenus);
    
    console.log('✅ Tree built and sorted:', rootMenus.length, 'root menus');
    console.log('📊 Root menu order:', rootMenus.map(m => `${m.menuName}(${m.displayOrder})`).join(' → '));
    return rootMenus;
  };

  const loadMenuItems = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.get(`${API_BASE_URL}/menus/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.data.success) {
        const menuItems = response.data.data || [];
        
        // 디버깅: API 응답 확인
        console.log('✅ API Response:', response.data);
        console.log('📊 Total menus:', menuItems.length);
        
        // 트리 구조인지 평면 리스트인지 확인
        const isTreeStructure = menuItems.some((menu: MenuItem) => 
          Array.isArray(menu.children) && menu.children.length > 0
        );
        
        console.log('🔍 Structure check:', isTreeStructure ? '✅ Already tree structure' : '📋 Flat list - needs conversion');
        
        let treeMenuItems: MenuItem[];
        
        if (isTreeStructure) {
          // 이미 트리 구조 - 그대로 사용
          console.log('✅ Using tree structure from backend');
          treeMenuItems = menuItems;
          
          // displayOrder로 정렬만 수행
          const sortByDisplayOrder = (items: MenuItem[]) => {
            items.sort((a, b) => a.displayOrder - b.displayOrder);
            items.forEach(item => {
              if (item.children && item.children.length > 0) {
                sortByDisplayOrder(item.children);
              }
            });
          };
          sortByDisplayOrder(treeMenuItems);
          console.log('📊 Sorted tree:', treeMenuItems.map(m => `${m.menuName}(${m.displayOrder})`).join(' → '));
        } else {
          // 평면 리스트 - 트리로 변환
          console.log('🔧 Converting flat list to tree...');
          treeMenuItems = buildMenuTree(menuItems);
        }
        
        // 변환 후 확인
        console.log('🌳 Tree structure:', treeMenuItems);
        treeMenuItems.forEach((menu: MenuItem) => {
          console.log(`📂 ${menu.menuName}:`, {
            id: menu.id,
            hasChildren: menu.children?.length > 0,
            childrenCount: menu.children?.length || 0,
            children: menu.children?.map((c: MenuItem) => c.menuName),
          });
        });
        
        // 자식 메뉴 있는 메뉴 확인
        const countChildren = (items: MenuItem[]): number => {
          let count = 0;
          items.forEach(item => {
            if (item.children && item.children.length > 0) {
              count++;
              count += countChildren(item.children);
            }
          });
          return count;
        };
        const withChildrenCount = countChildren(treeMenuItems);
        console.log('👶 Menus with children:', withChildrenCount);
        
        if (withChildrenCount === 0) {
          console.warn('⚠️ 자식 메뉴가 있는 메뉴가 없습니다!');
          console.log('💡 데이터베이스에서 parent_id가 NULL이 아닌 메뉴를 확인하세요.');
        } else {
          console.log('✅ Children 배열이 정상적으로 포함되어 있습니다!');
        }
        
        setMenuItems(treeMenuItems);
        
        // 초기 로드 시 모든 부모 메뉴 펼치기
        const allParentIds = new Set<number>();
        const collectParentIds = (items: MenuItem[]) => {
          items.forEach((item) => {
            // 방어 코드: children이 undefined일 경우 대비
            const children = item.children || [];
            if (children.length > 0) {
              allParentIds.add(item.id);
              collectParentIds(children);
            }
          });
        };
        collectParentIds(treeMenuItems);
        console.log('🔓 Initially expanded menus:', Array.from(allParentIds));
        setExpandedMenus(allParentIds);
        
        // 선택된 메뉴가 있으면 업데이트된 데이터로 갱신
        if (selectedMenu) {
          const updatedMenu = findMenuById(treeMenuItems, selectedMenu.id);
          if (updatedMenu) {
            setSelectedMenu(updatedMenu);
          }
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || '메뉴 목록을 불러오는데 실패했습니다.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!formData.menuCode.trim()) {
      setError('메뉴 코드를 입력해주세요.');
      return;
    }
    if (!formData.menuName.trim()) {
      setError('메뉴 이름을 입력해주세요.');
      return;
    }

    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.post(`${API_BASE_URL}/menus`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.data.success) {
        setSuccess('메뉴가 추가되었습니다.');
        setShowAddForm(false);
        setFormData({
          menuCode: '',
          menuName: '',
          menuPath: '',
          menuIcon: '',
          parentId: null,
          displayOrder: 1,
          menuLevel: 0,
          description: '',
          requiredRole: 'CLIENT',
          isActive: true,
          isExternal: false,
        });
        loadMenuItems();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || '메뉴 추가에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (id: number, data: Partial<MenuFormData>, fieldName?: string) => {
    try {
      if (fieldName) {
        setSavingField(fieldName);
      }
      setLoading(true);
      const token = getToken();
      
      // selectedMenu가 있으면 그것을 기반으로, 없으면 menuItems에서 찾기
      const baseMenu = selectedMenu?.id === id ? selectedMenu : findMenuById(menuItems, id);
      if (!baseMenu) {
        setError('메뉴를 찾을 수 없습니다.');
        return;
      }
      
      // 전체 데이터 구성 (PUT 요청을 위해 모든 필드 포함)
      const fullData: MenuFormData = {
        menuCode: baseMenu.menuCode,
        menuName: baseMenu.menuName,
        menuPath: baseMenu.menuPath || '',
        menuIcon: baseMenu.menuIcon || '',
        parentId: baseMenu.parentId,
        displayOrder: baseMenu.displayOrder,
        menuLevel: baseMenu.menuLevel,
        description: baseMenu.description || '',
        requiredRole: baseMenu.requiredRole,
        isActive: baseMenu.isActive,
        isExternal: baseMenu.isExternal,
        ...data, // 변경된 데이터로 오버라이드
      };
      
      const response = await axios.put(`${API_BASE_URL}/menus/${id}`, fullData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.data.success) {
        if (fieldName) {
          setSavingField(null);
          setSavedField(fieldName);
          // 3초 후 저장 완료 표시 제거
          setTimeout(() => {
            setSavedField(null);
          }, 3000);
        } else {
          setSuccess('메뉴가 수정되었습니다.');
        }
        loadMenuItems();
        // selectedMenu 업데이트
        if (selectedMenu?.id === id) {
          const updatedMenu = findMenuById(menuItems, id);
          if (updatedMenu) {
            setSelectedMenu(updatedMenu);
          }
        }
      }
    } catch (err: any) {
      if (fieldName) {
        setSavingField(null);
      }
      setError(err.response?.data?.message || '메뉴 수정에 실패했습니다.');
      // 에러 발생 시 원래 값으로 복구
      const originalMenu = findMenuById(menuItems, id);
      if (originalMenu && selectedMenu?.id === id) {
        setSelectedMenu(originalMenu);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('이 메뉴를 삭제하시겠습니까? 하위 메뉴가 있는 경우 삭제할 수 없습니다.')) {
      return;
    }

    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.delete(`${API_BASE_URL}/menus/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.success) {
        setSuccess('메뉴가 삭제되었습니다.');
        // 삭제된 메뉴가 선택되어 있었다면 선택 해제
        if (selectedMenu?.id === id) {
          setSelectedMenu(null);
        }
        loadMenuItems();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || '메뉴 삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id: number, isActive: boolean) => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.patch(
        `${API_BASE_URL}/menus/${id}/active?isActive=${isActive}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success) {
        setSuccess(`메뉴가 ${isActive ? '활성화' : '비활성화'}되었습니다.`);
        loadMenuItems();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || '메뉴 상태 변경에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const renderSaveIndicator = (fieldName: string) => {
    if (savingField === fieldName) {
      return (
        <span className="flex items-center gap-1 text-sm text-blue-600 animate-pulse">
          <Save className="w-4 h-4 animate-spin" />
          저장 중...
        </span>
      );
    }
    if (savedField === fieldName) {
      return (
        <span className="flex items-center gap-1 text-sm text-green-600">
          <CheckCircle2 className="w-4 h-4" />
          저장 완료
        </span>
      );
    }
    return null;
  };

  const isLeafNode = (item: MenuItem): boolean => {
    return !item.children || item.children.length === 0;
  };

  const toggleMenuExpand = (menuId: number) => {
    console.log('🔄 Toggle menu:', menuId);
    setExpandedMenus((prev) => {
      const newSet = new Set(prev);
      const wasExpanded = newSet.has(menuId);
      if (wasExpanded) {
        newSet.delete(menuId);
        console.log('➖ Collapsed menu:', menuId);
      } else {
        newSet.add(menuId);
        console.log('➕ Expanded menu:', menuId);
      }
      console.log('📋 Current expanded menus:', Array.from(newSet));
      return newSet;
    });
  };

  const expandAll = () => {
    console.log('📢 Expand All clicked');
    const allParentIds = new Set<number>();
    const collectParentIds = (items: MenuItem[]) => {
      items.forEach((item) => {
        // 방어 코드: children이 undefined일 경우 대비
        const children = item.children || [];
        if (children.length > 0) {
          allParentIds.add(item.id);
          console.log(`  ➕ Adding parent: ${item.menuName} (id: ${item.id})`);
          collectParentIds(children);
        }
      });
    };
    collectParentIds(menuItems);
    console.log('📂 Total parent menus to expand:', allParentIds.size);
    console.log('📋 Expanded menu IDs:', Array.from(allParentIds));
    setExpandedMenus(allParentIds);
  };

  const collapseAll = () => {
    console.log('📢 Collapse All clicked');
    console.log('➖ Clearing all expanded menus');
    setExpandedMenus(new Set());
  };

  const renderMenuTree = (items: MenuItem[], depth: number = 0) => {
    return items.map((item) => {
      // 방어 코드: children이 undefined일 경우 빈 배열로 처리
      const children = item.children || [];
      const hasChildren = Array.isArray(children) && children.length > 0;
      const isExpanded = expandedMenus.has(item.id);
      
      // 디버깅: 렌더링 정보
      if (process.env.NODE_ENV === 'development') {
        console.log(`🎨 Rendering: ${item.menuName}`, {
          depth,
          hasChildren,
          childrenCount: children.length,
          isExpanded,
          children: children.map((c: MenuItem) => c.menuName),
        });
      }

      return (
        <div key={item.id} className="space-y-1">
          {/* 메뉴 항목 컨테이너 - 전체에 들여쓰기 적용 */}
          <div 
            className="flex items-center gap-1"
            style={{ paddingLeft: `${depth * 1.5}rem` }}
          >
            {/* 펼치기/접기 버튼 (자식이 있는 경우만) */}
            {hasChildren ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleMenuExpand(item.id);
                }}
                className="p-1 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
                title={isExpanded ? '접기' : '펼치기'}
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-600" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                )}
              </button>
            ) : (
              <div className="w-6" /> // 빈 공간 (정렬용)
            )}

            {/* 메뉴 항목 */}
            <button
              onClick={() => {
                console.log('📌 Menu selected:', item.menuName, '(id:', item.id, ')');
                setSelectedMenu(item);
              }}
              className={`flex-1 text-left p-2 rounded-lg transition-colors flex items-center gap-2 ${
                selectedMenu?.id === item.id
                  ? 'bg-primary text-white'
                  : 'hover:bg-gray-100'
              } ${!item.isActive ? 'opacity-50' : ''}`}
            >
              {item.menuIcon && <span className="text-sm">{item.menuIcon}</span>}
              <span className="text-sm font-medium flex-1 truncate">{item.menuName}</span>
              {hasChildren && (
                <Badge className="text-xs bg-blue-100 text-blue-800 border-0">
                  {children.length}
                </Badge>
              )}
            </button>
          </div>

          {/* 자식 메뉴 (펼쳐진 경우에만 표시) */}
          {hasChildren && isExpanded && (
            <div>
              {renderMenuTree(children, depth + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  const flattenMenuItems = (items: MenuItem[]): { id: number; name: string; level: number }[] => {
    const result: { id: number; name: string; level: number }[] = [];
    const traverse = (items: MenuItem[], level: number = 0) => {
      items.forEach((item) => {
        result.push({ id: item.id, name: item.menuName, level });
        if (item.children && item.children.length > 0) {
          traverse(item.children, level + 1);
        }
      });
    };
    traverse(items);
    return result;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            {menuIcon ? <span className="text-4xl">{menuIcon}</span> : <MenuIcon className="w-8 h-8 text-primary" />}
            메뉴 구조 관리
          </h1>
          <p className="text-gray-600 mt-2">시스템 메뉴를 추가, 수정, 삭제할 수 있습니다</p>
        </div>
        <Button onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? (
            <>
              <X className="w-4 h-4 mr-2" />
              취소
            </>
          ) : (
            <>
              <Plus className="w-4 h-4 mr-2" />
              메뉴 추가
            </>
          )}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>새 메뉴 추가</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">메뉴 코드 *</label>
                <Input
                  value={formData.menuCode}
                  onChange={(e) => setFormData({ ...formData, menuCode: e.target.value.toUpperCase() })}
                  placeholder="예: DASHBOARD"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">메뉴 이름 *</label>
                <Input
                  value={formData.menuName}
                  onChange={(e) => setFormData({ ...formData, menuName: e.target.value })}
                  placeholder="예: 대시보드"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">메뉴 경로</label>
                <Input
                  value={formData.menuPath}
                  onChange={(e) => setFormData({ ...formData, menuPath: e.target.value })}
                  placeholder="예: /dashboard"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">메뉴 아이콘</label>
                <Input
                  value={formData.menuIcon}
                  onChange={(e) => setFormData({ ...formData, menuIcon: e.target.value })}
                  placeholder="예: 📊"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">상위 메뉴</label>
              <select
                value={formData.parentId || ''}
                onChange={(e) => {
                  const parentId = e.target.value ? Number(e.target.value) : null;
                  const menuLevel = parentId ? (menuItems.find(m => m.id === parentId)?.menuLevel || 0) + 1 : 0;
                  setFormData({ ...formData, parentId, menuLevel });
                }}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="">최상위 메뉴</option>
                {flattenMenuItems(menuItems).map((item) => (
                  <option key={item.id} value={item.id}>
                    {'　'.repeat(item.level * 2)}
                    {item.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">설명</label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="메뉴 설명을 입력하세요"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">표시 순서</label>
                <Input
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) => setFormData({ ...formData, displayOrder: Number(e.target.value) })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">권한</label>
                <select
                  value={formData.requiredRole}
                  onChange={(e) =>
                    setFormData({ ...formData, requiredRole: e.target.value as 'SUPER_ADMIN' | 'CLIENT' })
                  }
                  className="w-full border rounded-md px-3 py-2"
                >
                  <option value="CLIENT">일반 사용자</option>
                  <option value="SUPER_ADMIN">관리자</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">상태</label>
                <select
                  value={formData.isActive ? 'true' : 'false'}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.value === 'true' })
                  }
                  className="w-full border rounded-md px-3 py-2"
                >
                  <option value="true">활성</option>
                  <option value="false">비활성</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isExternal"
                checked={formData.isExternal}
                onChange={(e) => setFormData({ ...formData, isExternal: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="isExternal" className="text-sm">
                외부 링크
              </label>
            </div>

            <Button onClick={handleAdd} disabled={loading} className="w-full">
              <Save className="w-4 h-4 mr-2" />
              저장
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 왼쪽: 메뉴 트리 */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>메뉴 트리</CardTitle>
                <CardDescription>메뉴를 선택하여 상세 정보를 확인하세요</CardDescription>
              </div>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={expandAll}
                  title="모두 펼치기"
                  className="text-xs h-7 px-2"
                >
                  <ChevronDown className="w-3 h-3 mr-1" />
                  펼치기
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={collapseAll}
                  title="모두 접기"
                  className="text-xs h-7 px-2"
                >
                  <ChevronRight className="w-3 h-3 mr-1" />
                  접기
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="max-h-[600px] overflow-y-auto">
            {loading && menuItems.length === 0 ? (
              <div className="text-center py-8 text-gray-500">로딩 중...</div>
            ) : menuItems.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                등록된 메뉴가 없습니다.
              </div>
            ) : (
              <div className="space-y-1">{renderMenuTree(menuItems)}</div>
            )}
          </CardContent>
        </Card>

        {/* 오른쪽: 상세 정보 */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>상세 정보</CardTitle>
            <CardDescription>
              {selectedMenu ? '각 필드는 수정 시 자동으로 저장됩니다' : '메뉴를 선택해주세요'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {selectedMenu ? (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 pb-4 border-b">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {selectedMenu.menuIcon && (
                      <span className="text-3xl">{selectedMenu.menuIcon}</span>
                    )}
                    <div className="min-w-0">
                      <h3 className="text-xl font-bold">{selectedMenu.menuName}</h3>
                      <p className="text-sm text-gray-500">코드: {selectedMenu.menuCode}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleToggleActive(selectedMenu.id, !selectedMenu.isActive)}
                      title={selectedMenu.isActive ? '메뉴를 비활성화합니다' : '메뉴를 활성화합니다'}
                    >
                      {selectedMenu.isActive ? (
                        <>
                          <Power className="w-4 h-4 mr-2 text-green-600" />
                          활성화 중
                        </>
                      ) : (
                        <>
                          <PowerOff className="w-4 h-4 mr-2 text-gray-400" />
                          비활성화 중
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(selectedMenu.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      삭제
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium">메뉴 코드 *</label>
                      {renderSaveIndicator('menuCode')}
                    </div>
                    <Input
                      value={selectedMenu.menuCode}
                      onChange={(e) => {
                        setSelectedMenu({ ...selectedMenu, menuCode: e.target.value });
                      }}
                      onBlur={(e) => {
                        const trimmedValue = e.target.value.trim();
                        if (!trimmedValue) {
                          setError('메뉴 코드는 필수입니다.');
                          // 원래 값으로 복구
                          const originalMenu = findMenuById(menuItems, selectedMenu.id);
                          if (originalMenu) {
                            setSelectedMenu({ ...selectedMenu, menuCode: originalMenu.menuCode });
                          }
                          return;
                        }
                        const originalMenu = findMenuById(menuItems, selectedMenu.id);
                        if (originalMenu && trimmedValue !== originalMenu.menuCode) {
                          handleUpdate(selectedMenu.id, { menuCode: trimmedValue }, 'menuCode');
                        }
                      }}
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium">메뉴 이름 *</label>
                      {renderSaveIndicator('menuName')}
                    </div>
                    <Input
                      value={selectedMenu.menuName}
                      onChange={(e) => {
                        setSelectedMenu({ ...selectedMenu, menuName: e.target.value });
                      }}
                      onBlur={(e) => {
                        const trimmedValue = e.target.value.trim();
                        if (!trimmedValue) {
                          setError('메뉴 이름은 필수입니다.');
                          // 원래 값으로 복구
                          const originalMenu = findMenuById(menuItems, selectedMenu.id);
                          if (originalMenu) {
                            setSelectedMenu({ ...selectedMenu, menuName: originalMenu.menuName });
                          }
                          return;
                        }
                        const originalMenu = findMenuById(menuItems, selectedMenu.id);
                        if (originalMenu && trimmedValue !== originalMenu.menuName) {
                          handleUpdate(selectedMenu.id, { menuName: trimmedValue }, 'menuName');
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium">
                        메뉴 경로 {isLeafNode(selectedMenu) && <span className="text-red-500">*</span>}
                        {!isLeafNode(selectedMenu) && (
                          <span className="text-xs text-red-500 ml-2">
                            (최하위 메뉴에서만 설정 가능)
                          </span>
                        )}
                      </label>
                      {renderSaveIndicator('menuPath')}
                    </div>
                    <Input
                      value={selectedMenu.menuPath || ''}
                      onChange={(e) => {
                        setSelectedMenu({ ...selectedMenu, menuPath: e.target.value });
                      }}
                      onBlur={(e) => {
                        if (!isLeafNode(selectedMenu)) return;
                        
                        const trimmedValue = e.target.value.trim();
                        // 최하위 메뉴는 경로가 필수
                        if (!trimmedValue) {
                          setError('최하위 메뉴는 경로가 필수입니다.');
                          // 원래 값으로 복구
                          const originalMenu = findMenuById(menuItems, selectedMenu.id);
                          if (originalMenu) {
                            setSelectedMenu({ ...selectedMenu, menuPath: originalMenu.menuPath });
                          }
                          return;
                        }
                        const originalMenu = findMenuById(menuItems, selectedMenu.id);
                        if (originalMenu && trimmedValue !== originalMenu.menuPath) {
                          handleUpdate(selectedMenu.id, { menuPath: trimmedValue }, 'menuPath');
                        }
                      }}
                      disabled={!isLeafNode(selectedMenu)}
                      placeholder={isLeafNode(selectedMenu) ? '예: /dashboard' : '하위 메뉴가 있어 경로 설정 불가'}
                      className={!isLeafNode(selectedMenu) ? 'bg-gray-100 cursor-not-allowed' : ''}
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium">메뉴 아이콘</label>
                      {renderSaveIndicator('menuIcon')}
                    </div>
                    <Input
                      value={selectedMenu.menuIcon || ''}
                      onChange={(e) => {
                        setSelectedMenu({ ...selectedMenu, menuIcon: e.target.value });
                      }}
                      onBlur={(e) => {
                        const trimmedValue = e.target.value.trim();
                        const originalMenu = findMenuById(menuItems, selectedMenu.id);
                        if (originalMenu && trimmedValue !== (originalMenu.menuIcon || '')) {
                          handleUpdate(selectedMenu.id, { menuIcon: trimmedValue || undefined }, 'menuIcon');
                        }
                      }}
                      placeholder="예: 📊"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium">설명</label>
                    {renderSaveIndicator('description')}
                  </div>
                  <Input
                    value={selectedMenu.description || ''}
                    onChange={(e) => {
                      setSelectedMenu({ ...selectedMenu, description: e.target.value });
                    }}
                    onBlur={(e) => {
                      const trimmedValue = e.target.value.trim();
                      const originalMenu = findMenuById(menuItems, selectedMenu.id);
                      if (originalMenu && trimmedValue !== (originalMenu.description || '')) {
                        handleUpdate(selectedMenu.id, { description: trimmedValue || undefined }, 'description');
                      }
                    }}
                    placeholder="메뉴 설명을 입력하세요"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium">상위 메뉴</label>
                    {renderSaveIndicator('parentId')}
                  </div>
                  <select
                    value={selectedMenu.parentId || ''}
                    onChange={(e) => {
                      const newParentId = e.target.value ? Number(e.target.value) : null;
                      // 부모의 레벨을 찾아서 자신의 레벨 계산
                      let newMenuLevel = 0;
                      if (newParentId) {
                        const parent = findMenuById(menuItems, newParentId);
                        newMenuLevel = parent ? parent.menuLevel + 1 : 0;
                      }
                      
                      // 하위 메뉴가 있으면 경로를 undefined로 설정
                      const hasChildren = selectedMenu.children && selectedMenu.children.length > 0;
                      const menuPath = hasChildren ? undefined : (selectedMenu.menuPath || undefined);
                      
                      setSelectedMenu({ 
                        ...selectedMenu, 
                        parentId: newParentId,
                        menuLevel: newMenuLevel,
                        menuPath: menuPath ?? null
                      });
                      handleUpdate(selectedMenu.id, { 
                        parentId: newParentId,
                        menuLevel: newMenuLevel,
                        menuPath: menuPath
                      }, 'parentId');
                    }}
                    className="w-full border rounded-md px-3 py-2"
                  >
                    <option value="">최상위 메뉴</option>
                    {flattenMenuItems(menuItems)
                      .filter(item => {
                        // 자기 자신과 자신의 하위 메뉴는 선택할 수 없음
                        if (item.id === selectedMenu.id) return false;
                        const isChildOfSelected = (menuId: number): boolean => {
                          const menu = findMenuById(menuItems, menuId);
                          if (!menu) return false;
                          if (menu.parentId === selectedMenu.id) return true;
                          if (menu.parentId) return isChildOfSelected(menu.parentId);
                          return false;
                        };
                        return !isChildOfSelected(item.id);
                      })
                      .map((item) => (
                        <option key={item.id} value={item.id}>
                          {'　'.repeat(item.level * 2)}
                          {item.name}
                        </option>
                      ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    상위 메뉴를 변경하면 레벨이 자동으로 조정됩니다
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium">표시 순서</label>
                      {renderSaveIndicator('displayOrder')}
                    </div>
                    <Input
                      type="number"
                      value={selectedMenu.displayOrder}
                      onChange={(e) => {
                        setSelectedMenu({ ...selectedMenu, displayOrder: Number(e.target.value) });
                      }}
                      onBlur={(e) => {
                        const originalMenu = findMenuById(menuItems, selectedMenu.id);
                        if (originalMenu && Number(e.target.value) !== originalMenu.displayOrder) {
                          handleUpdate(selectedMenu.id, { displayOrder: Number(e.target.value) }, 'displayOrder');
                        }
                      }}
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium">권한</label>
                      {renderSaveIndicator('requiredRole')}
                    </div>
                    <select
                      value={selectedMenu.requiredRole}
                      onChange={(e) => {
                        const requiredRole = e.target.value as 'SUPER_ADMIN' | 'CLIENT';
                        setSelectedMenu({ ...selectedMenu, requiredRole });
                        handleUpdate(selectedMenu.id, { requiredRole }, 'requiredRole');
                      }}
                      className="w-full border rounded-md px-3 py-2"
                    >
                      <option value="CLIENT">일반 사용자</option>
                      <option value="SUPER_ADMIN">관리자</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      레벨 <span className="text-xs text-gray-500">(자동 계산)</span>
                    </label>
                    <Input
                      type="number"
                      value={selectedMenu.menuLevel}
                      disabled
                      className="bg-gray-100"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isExternal"
                      checked={selectedMenu.isExternal}
                      onChange={(e) => {
                        setSelectedMenu({ ...selectedMenu, isExternal: e.target.checked });
                        handleUpdate(selectedMenu.id, { isExternal: e.target.checked }, 'isExternal');
                      }}
                      className="w-4 h-4"
                    />
                    <label htmlFor="isExternal" className="text-sm">
                      외부 링크
                    </label>
                  </div>
                  {renderSaveIndicator('isExternal')}
                </div>

                <div className="pt-4 border-t">
                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                    <div>
                      <span className="font-medium">생성일:</span>{' '}
                      {new Date(selectedMenu.createdAt).toLocaleString('ko-KR')}
                    </div>
                    {selectedMenu.updatedAt && (
                      <div>
                        <span className="font-medium">수정일:</span>{' '}
                        {new Date(selectedMenu.updatedAt).toLocaleString('ko-KR')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <MenuIcon className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>왼쪽 트리에서 메뉴를 선택하면</p>
                <p>상세 정보가 표시됩니다</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};